import { XIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'

import type { DesiredLocation } from 'src/features/consumers/schemas/consumer'
import { useMapApiKey } from 'src/shared/api/google-maps/use-map-api-key'

import { SearchIcon } from '../icons/search-icon'

import DropdownLocationListItem from './ui/dropdown-location-item'
import DropdownPredictionContainer from './ui/dropdown-prediction-container'

export interface PlaceResult {
  street: string
  city: string
  state: string
  zip: string
  latitude: number
  longitude: number
  placeId: string
}

interface BusinessDesiredLocationSelectorProps {
  onPlaceSelected: (place: PlaceResult) => void
  selectedLocations: (DesiredLocation & { placeId: string })[]
  placeholder?: string
  componentRestrictions?: google.maps.places.ComponentRestrictions
  align?: 'top' | 'bottom'
}

export default function BusinessDesiredLocationSelector({
  onPlaceSelected,
  placeholder = 'Search city, state, zip...',
  componentRestrictions = { country: 'us' },
  selectedLocations,
  align = 'bottom',
}: BusinessDesiredLocationSelectorProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [inputValue, setInputValue] = useState<string>('')
  const [showPredictions, setShowPredictions] = useState<boolean>(false)
  const [selectedIndex, setSelectedIndex] = useState<number>(-1)
  const { data: mapApiKey } = useMapApiKey()

  const { placesService, placePredictions, getPlacePredictions } = usePlacesService({
    apiKey: mapApiKey,
    debounce: 500,
  })

  useEffect(() => {
    // Reset selected index when predictions change
    setSelectedIndex(-1)
  }, [placePredictions])

  const handlePlaceDetails = (placeId: string) => {
    if (!placesService) return

    placesService.getDetails(
      {
        placeId: placeId,
      },
      (placeDetails: google.maps.places.PlaceResult | null) => {
        if (!placeDetails) return

        const addressComponents = placeDetails.address_components || []
        const geometry = placeDetails.geometry || {}

        const getComponent = (types: string[]) => {
          for (const type of types) {
            const component = addressComponents.find((component) => component.types.includes(type))
            if (component) {
              if (type === 'administrative_area_level_1' || type === 'administrative_area_level_2') {
                return component.short_name
              }
              return component.long_name
            }
          }
          return ''
        }

        const streetNumber = getComponent(['street_number'])
        const route = getComponent(['route'])
        const street = `${streetNumber} ${route}`.trim()
        const city = getComponent([
          'locality',
          'postal_town',
          'sublocality',
          'neighborhood',
          'administrative_area_level_2',
        ])
        const state = getComponent(['administrative_area_level_1', 'administrative_area_level_2'])
        const zip = getComponent(['postal_code'])

        const placeResult: PlaceResult = {
          street,
          city,
          state,
          zip,
          latitude: geometry.location?.lat() || 0,
          longitude: geometry.location?.lng() || 0,
          placeId: placeId,
        }

        onPlaceSelected(placeResult)

        if (inputRef.current) {
          const displayValue = `${city}, ${state}`

          inputRef.current.value = displayValue
          setInputValue(displayValue)
        }
        setShowPredictions(false)
        setSelectedIndex(-1)
      },
    )
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    setShowPredictions(true)
    if (value.length > 1) {
      getPlacePredictions({
        input: value,
        types: ['(cities)'],
        componentRestrictions,
      })
    } else {
      setShowPredictions(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showPredictions || !placePredictions.length) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => (prev < placePredictions.length - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0) {
          handlePlaceDetails(placePredictions[selectedIndex].place_id)
        }
        break
      case 'Escape':
        setShowPredictions(false)
        setSelectedIndex(-1)
        break
    }
  }

  // Handle clicks outside the component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowPredictions(false)
        setSelectedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div className="relative z-[70] flex w-[500px] items-end self-end">
      <div className="relative z-10 flex h-[65px] w-full flex-row items-center gap-4 rounded-full bg-accent px-3">
        <SearchIcon size={20} className="ml-3 shrink-0 text-primary" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (inputValue.length > 2) {
              setShowPredictions(true)
            }
          }}
          className={'size-full rounded-full bg-accent text-lg placeholder:text-muted focus-visible:outline-none'}
          placeholder={placeholder}
          autoComplete="off"
          data-lpignore="true"
          data-form-type="other"
          role="combobox"
          aria-expanded={showPredictions}
          aria-controls="location-predictions"
          aria-autocomplete="list"
        />
        {inputValue && (
          <button
            className="flex min-h-[35px] min-w-[35px] items-center justify-center rounded-full bg-white"
            onClick={() => setInputValue('')}
          >
            <XIcon className="size-5" />
          </button>
        )}
      </div>
      {showPredictions && placePredictions.length > 0 && (
        <DropdownPredictionContainer ref={dropdownRef} align={align}>
          {placePredictions.map((prediction) => (
            <DropdownLocationListItem
              key={prediction.place_id}
              description={prediction.description}
              placeId={prediction.place_id}
              onSelectItem={handlePlaceDetails}
              isSelected={selectedLocations.some((l) => l.placeId === prediction.place_id)}
              subtitle="City, state"
            />
          ))}
        </DropdownPredictionContainer>
      )}
    </div>
  )
}
